import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Select } from '@mui/material';
// utils
import { formatEnum } from '../../utils/string';
import RoleScopeEnum from '../../enums/RoleScopeEnum';
import { refreshUserSession, handleSessionRefreshFailure } from '../../utils/sessionManager';
import { useNavigate } from 'react-router-dom';
import { updateUserInProject } from '../../utils/api/user_apis';
import { getRolesForProject } from '../../utils/api/role_apis';
import { STATUS } from '../../utils/api/sharedfunction';
// components
import SelfEditConfirmationModal from './SelfEditConfirmationModal';
import { CommonFooter, FormSchemas, FormWrapper } from '../FormComponent';
import ModalWrapper from '../ModalWrapper';

const EditProjectUserModal = ({ isOpen, onClose, onUpdate, onError, data }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSelfEditConfirmation, setShowSelfEditConfirmation] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const currentUser = useSelector((state) => state.auth.user);
  
  // Check if user is editing themselves
  const isEditingSelf = data?.id === currentUser?.id;

  useEffect(() => {
    if (isOpen && selectedProject?.id) {
      fetchRoles();
    }
  }, [isOpen, selectedProject?.id]);

  const handleFormSubmit = async () => {
    if (formRef.current) {
      await formRef.current.submit();
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await getRolesForProject(RoleScopeEnum.PROJECT_LEVEL, selectedProject.id);

      if (response.status === STATUS.SUCCESS) {
        const projectRoles = response?.data?.roles || [];
        setRoles(
          projectRoles.map((role) => ({
            id: role.id,
            name: role.name,
          }))
        );
      } else {
        console.error('Failed to fetch roles:', response.error);
        setRoles([]); 
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      setRoles([]); 
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEdit = async (formData) => {
    // If user is editing themselves, show confirmation dialog first
    if (isEditingSelf) {
      setPendingFormData(formData);
      setShowSelfEditConfirmation(true);
      return;
    }
    
    // For other users, proceed directly
    await performRoleUpdate(formData);
  };

  const handleSelfEditConfirm = async () => {
    setShowSelfEditConfirmation(false);
    await performRoleUpdate(pendingFormData);
    setPendingFormData(null);
  };

  const handleSelfEditCancel = () => {
    setShowSelfEditConfirmation(false);
    setPendingFormData(null);
    setLoading(false);
  };

  const performRoleUpdate = async (formData) => {
    try {
      setLoading(true);

      const response = await updateUserInProject({
        userId: data?.id,
        projectId: selectedProject.id,
        roleIds: formData.role,
      });

      if (response.status === STATUS.SUCCESS) {
        // If editing self and response indicates current user was updated
        if (isEditingSelf && response.data?.isCurrentUser) {
          await handleSessionRefresh();
        }

        onUpdate();
        onClose();
      } else {
        onError(response.error || 'Failed to update user roles', 'error');
      }
    } catch (error) {
      console.error('Error updating user roles:', error);
      onError('Failed to update user roles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionRefresh = async () => {
    try {
      const result = await refreshUserSession(dispatch);
      
      if (!result.success) {
        // Handle session refresh failure
        handleSessionRefreshFailure(dispatch, navigate, onError, result);
      }
    } catch (error) {
      console.error('Session refresh error:', error);
      onError('Session update failed. Please refresh the page or re-login.', 'warning');
    }
  };

  const buttons = [
    {
      label: 'Cancel',
      variant: 'outline',
      className: 'primary',
      onClick: onClose,
      disabled: loading,
      loading: loading,
    },
    {
      label: 'Update',
      variant: 'primary',
      className: 'white bg-blue600',
      disabled: loading,
      loading: loading,
      onClick: handleFormSubmit,
    },
  ];

  const fields = [
    {
      name: 'user',
      type: 'select',
      label: 'User',
      required: true,
      options: [{
        value: data?.id,
        label: `${data?.username} (${data?.email})`,
      }],
    },
    {
      name: 'role',
      type: 'select',
      label: 'Role',
      required: true,
      multiple: true,
      showCheckboxes: true,
      options: roles.map((role) => ({
        value: role.id,
        label: formatEnum(role.name),
      })),
    },
  ];

  // Get role names for confirmation dialog
  const getCurrentRoleNames = () => {
    return data?.roles?.map(role => role.name) || [];
  };

  const getNewRoleNames = () => {
    if (!pendingFormData?.role || !roles.length) return [];
    return roles
      .filter(role => pendingFormData.role.includes(role.id))
      .map(role => role.name);
  };

  return (
    <>
      <ModalWrapper
        isOpen={isOpen}
        onClose={onClose}
        title={isEditingSelf ? "Edit Your Roles" : "Edit User"}
        size="small"
        footer={
          <CommonFooter
            buttons={buttons}
            align="right"
          />
        }
        children={
          <FormWrapper
            ref={formRef}
            schema={FormSchemas.addUserToProject}
            fields={fields}
            onSubmit={handleConfirmEdit}
            defaultValues={{
              user: data?.id || '',
              role: data?.roles?.map((r) => r.id) || [],
            }}
          />
        }
      />

      {/* Self-edit confirmation dialog */}
      <SelfEditConfirmationModal
        open={showSelfEditConfirmation}
        onClose={handleSelfEditCancel}
        onConfirm={handleSelfEditConfirm}
        currentRoles={getCurrentRoleNames()}
        newRoles={getNewRoleNames()}
        loading={loading}
      />
    </>
  );
};

export default EditProjectUserModal;
